/**
 * GALSSR (2026-08-01) — /gallery was invisible to non-rendering crawlers.
 *
 * `/gallery` is `force-dynamic` and hands every item to a `"use client"` grid
 * whose thumbnails are gated behind IntersectionObserver *state*
 * (`{inView && <Image/>}`) and whose initial `displayCount` is 6. Verified live:
 * 718,764 bytes of HTML carrying ~775 escaped `"title":"…"` strings — all of
 * them inside `self.__next_f` RSC flight `<script>` blocks — but only a handful
 * of `<img>` tags, 44 `animate-pulse` skeletons, and 477 visible words. Video
 * items contribute no `<img>` at all (they render through `LazyVideo`), so a
 * video-led first row produced a page whose only content word was "Loading".
 *
 * Googlebot executes JS, so Search Console never flagged this. The ChatGPT /
 * Claude / Perplexity / Copilot fetchers read raw HTML and do not — and
 * `/gallery` is one of 9 header nav items, `priority: 0.9` in the sitemap, and
 * the single `<url>` representing ALL gallery imagery in `sitemap-images.xml`.
 *
 * The fix server-renders the first screen as plain `<figure>` / `<img>` markup
 * (`GalleryStaticGrid`) and swaps it for the interactive grid on hydration.
 * This module holds the three things the two grids MUST agree on, so they
 * cannot drift:
 *
 *   1. `GALLERY_GRID_CLASS` — a column-count or gap mismatch reflows every tile
 *      the instant the interactive grid mounts (visible jump + CLS). Same
 *      reasoning as the note in `app/(main)/gallery/loading.tsx`.
 *   2. `getGalleryThumbnailUrl` — a URL mismatch means every above-the-fold
 *      thumbnail is downloaded TWICE (once for the static grid, once again
 *      after hydration).
 *   3. `galleryItemAlt` — a mismatch changes the accessibility tree under a
 *      screen-reader user at hydration time.
 *
 * NOTE: this file must stay isomorphic (no server-only imports) — it is
 * imported by both a server component and a `"use client"` component.
 */

import { getOptimizedImageUrl } from "@/lib/image-optimize";
import { isVideoKey } from "@/lib/media";
import type { GalleryItem } from "@/lib/gallery-types";

/**
 * The gallery grid's classes, shared by the server-rendered static grid and the
 * interactive client grid. Keep in sync with the 12-tile skeleton in
 * `app/(main)/gallery/loading.tsx` (2 / 4 / 5 / 6 columns).
 */
export const GALLERY_GRID_CLASS =
  "mt-8 grid grid-cols-2 gap-3 sm:mt-10 sm:gap-4 md:grid-cols-4 md:gap-5 xl:grid-cols-5 xl:gap-6 2xl:grid-cols-6";

/**
 * The subset of a gallery item both grids need.
 *
 * GALCPU-1 (2026-08-02): this carries the R2 `imageKey` and NOT a set of
 * pre-built absolute URLs. `/gallery` is `force-dynamic` and hands the WHOLE
 * index to a client component, so every field here is serialised into the RSC
 * flight payload once per item and paid in Worker CPU on every single view.
 *
 * Measured against production on 2026-08-02: **1,084 items, 738,770 bytes of
 * HTML of which 627,079 (84.9%) was inline RSC flight**. Three URL fields —
 * `imageUrl` (same-origin proxy), `imageUrlLightbox` and `imageUrlRaw` (both the
 * CDN URL, byte-identical to each other on all 899 image items) — accounted for
 * **287,896 bytes, 64.5% of all item payload**, to express what one ~48-byte key
 * plus a single page-level base URL already determines.
 *
 * So: send the key, derive the URLs. The CDN base is passed ONCE per page as a
 * prop, never per item. Note the key is NOT derivable from `id` either — only
 * 409 of 1,084 keys have a basename equal to their item id, so dropping it in
 * favour of the id would 404 the other 675.
 *
 * ── FIELD NOTES (they moved here when the shape became a `Pick<>`) ───────────
 *
 * `alt` — admin-authored accessible alt text; wins over every fallback in
 * `galleryItemAlt`. It is **OMITTED when absent, never set to `undefined`**:
 * React Flight serialises an explicit `undefined` as the 12-byte string
 * `"$undefined"`, which cost 20,140 bytes across the 1,060 items that have no
 * authored alt. `?` on the parent expresses "may be absent" — it does NOT stop
 * a producer writing the key with an `undefined` value, so the omission is a
 * measured production requirement of `app/(main)/gallery/page.tsx`, guarded by
 * GALCPU-4, not something the type can enforce.
 *
 * `imageKey` — the R2 object key (e.g. `gallery/<uuid>.webp`). Every URL — AND
 * the image/video distinction — is derived from this plus the page-level
 * `cdnBase`. See `galleryMediaUrl` and `galleryItemIsVideo`.
 *
 * Derived from the canonical `GalleryItem` (`lib/gallery-types.ts`) rather than
 * re-declared, so a rename on the parent fails the type-check here instead of
 * silently leaving this view behind.
 */
export type GalleryPrerenderItem = Pick<
  GalleryItem,
  "id" | "title" | "alt" | "category" | "imageKey"
>;

/**
 * Is this item a video? Derived from the R2 key's extension.
 *
 * GALMEDIA-1 (2026-08-02): this used to travel as a `mediaType: "image" |
 * "video"` field on every item. Measured on the live page 2026-08-02:
 * **1,078 occurrences in the RSC flight = 26,925 of the 733,796 HTML bytes
 * (3.7%)** — serialised on EVERY view, because the page is `force-dynamic`.
 *
 * It is 100% redundant: all 1,084 stored values agree with
 * `isVideoKey(imageKey)`, and they cannot disagree by construction.
 * Both upload routes (`app/api/admin/gallery/route.ts` and `.../bulk/route.ts`)
 * accept exactly six MIME types and derive BOTH the stored `mediaType` and the
 * key's extension from that same MIME through one `EXT_MAP` — video mimes only
 * ever produce `mp4`/`webm`/`mov` (all in `VIDEO_EXTENSIONS`) and image mimes
 * only `jpg`/`png`/`webp` (none of them). `lib/gallery.ts`'s `getMediaType`
 * already falls back to exactly this for legacy items with no stored value, and
 * the SEO/feed layer has used `isVideoKey` as its single source of truth since
 * the 2026-07 media-detection consolidation (`lib/media.ts`).
 *
 * Guarded by `tests/unit/components/gallery/gallery-media-derivation.spec.ts`,
 * which fails if the upload allow-list ever grows a MIME whose extension
 * classifies differently from its `mediaType`.
 */
export function galleryItemIsVideo(item: { imageKey?: string | null }): boolean {
  return isVideoKey(item.imageKey);
}

/**
 * Same-origin Worker proxy URL for an R2 media key.
 *
 * The fallback when no CDN base is configured (local dev without
 * `R2_PUBLIC_URL`), and the `src` a `<video>` tile uses so a CDN blip still has
 * an origin to fall back to. Mirrors `getGalleryItemImageUrl` in `lib/gallery.ts`
 * — that one is server-side, this one must stay isomorphic.
 */
export function galleryProxyUrl(imageKey: string): string {
  if (!imageKey) return "";
  return `/api/gallery/image?key=${encodeURIComponent(imageKey)}`;
}

/**
 * Full-size media URL for a gallery item.
 *
 * `cdnBase` is the R2 public base (`R2_PUBLIC_URL`, e.g.
 * `https://cdn.thefloralmuse.uk`). It is a **public CDN hostname declared in
 * `wrangler.jsonc` `vars`, not a secret** — and it was already being emitted
 * 1,084 times per page before GALCPU-1, so passing it once is a strict
 * reduction in exposure. It is passed down as a PROP rather than read from
 * `lib/r2.ts` in the browser, because `R2_PUBLIC_URL` is a Worker var and not
 * `NEXT_PUBLIC_*`: a client-side `getR2ImageUrl()` would resolve to the proxy
 * while the same component's SSR pass resolved to the CDN, which is a hydration
 * mismatch (the exact trap documented in `lib/r2.ts`).
 *
 * Prefers the CDN — `/api/*` is behind a WAF rule that 403s bot user-agents plus
 * a per-IP rate limit, and every proxy hit costs a Worker invocation and an R2
 * class-B read that `cdn.thefloralmuse.uk` serves from the edge for free.
 */
export function galleryMediaUrl(imageKey: string, cdnBase?: string): string {
  if (!imageKey) return "";
  const base = cdnBase?.replace(/\/$/, "");
  if (!base) return galleryProxyUrl(imageKey);
  return `${base}${imageKey.startsWith("/") ? "" : "/"}${imageKey}`;
}

/**
 * Build the grid thumbnail URL via the shared, feature-gated helper. When
 * Cloudflare Image Resizing is disabled this is a safe passthrough that returns
 * the raw R2 URL — so we never emit a Cloudflare Image-Resizing URL that 404s
 * on the zone. When resizing is enabled it produces a width/quality-optimised
 * `/cdn-cgi/image/…` derivative, which `lib/cloudflare-image-loader.ts` passes
 * through untouched — so a plain `<img src>` and a `next/image` with the same
 * input resolve to the SAME bytes, and the hydration swap costs no extra fetch.
 *
 * The defaults are load-bearing: they must match what `LazyThumbnail` in
 * `GalleryClient` requests, or the static grid warms the wrong cache entry.
 */
export function getGalleryThumbnailUrl(fullUrl: string, width = 400, quality = 60): string {
  if (!fullUrl) return fullUrl;
  return getOptimizedImageUrl(fullUrl, { width, quality, format: "auto" });
}

/**
 * Category → English subject for generated alt text.
 *
 * Gallery titles are Chinese social-media captions with a date prefix (e.g.
 * `20250831 🇬🇧利兹花店 爱你不是两三天`), which make poor English alt text. We
 * do NOT invent descriptions of images nobody has looked at — we state only
 * what the item's own category already asserts. The admin-authored `alt` field
 * always wins; the title is used only when there is nothing better.
 */
const CATEGORY_ALT_SUBJECT: Record<string, string> = {
  fresh: "Fresh flower bouquet",
  preserved: "Preserved flower arrangement",
  crochet: "Hand-crocheted flower arrangement",
  accessories: "Floral gift accessory",
};

const ALT_SUFFIX = "by The Floral Muse, Leeds florist";

/** Resolve the alt text (and, for videos, the accessible name) of a gallery item. */
export function galleryItemAlt(item: {
  alt?: string;
  title?: string;
  category?: string;
}): string {
  const authored = item.alt?.trim();
  if (authored) return authored;

  const category = item.category?.trim();
  if (category) {
    const subject =
      CATEGORY_ALT_SUBJECT[category.toLowerCase()] ?? `${category} floral arrangement`;
    return `${subject} ${ALT_SUFFIX}`;
  }

  const title = item.title?.trim();
  if (title) return title;

  return `Floral arrangement ${ALT_SUFFIX}`;
}

/**
 * How many leading items to server-render.
 *
 * `minItems` (12) covers the widest breakpoint's first page (2xl = 6×2), which
 * is also what `loading.tsx` reserves — so the static grid, the loading
 * skeleton and the interactive grid's first screen all show the same tile count
 * and nothing reflows.
 *
 * The window then EXTENDS (up to `maxItems`) until it covers `minImages` real
 * image items, because video items render as a placeholder and contribute no
 * `<img>` — a gallery whose first two rows are reels would otherwise
 * server-render almost no images, which is the exact defect being fixed.
 */
export function pickGalleryPrerenderCount(
  items: readonly { imageKey?: string | null }[],
  options: { minItems?: number; minImages?: number; maxItems?: number } = {},
): number {
  const minItems = options.minItems ?? 12;
  const minImages = options.minImages ?? 12;
  const maxItems = Math.max(options.maxItems ?? 24, minItems);

  const cap = Math.min(items.length, maxItems);
  let images = 0;
  let count = 0;
  for (let i = 0; i < cap; i++) {
    count = i + 1;
    const item = items[i];
    if (item && !galleryItemIsVideo(item)) images++;
    if (count >= minItems && images >= minImages) break;
  }
  return count;
}
